import * as React from 'react'
import * as Tgcc from '../tgccGlobal'
import * as Request from '../tgccRequest'
import { CSSProperties } from 'react'

const checkboxContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '20px auto',
    padding: '12px 16px',
    backgroundColor: 'rgba(248, 249, 250, 0.8)',
    borderRadius: '8px',
    border: '1px solid rgba(222, 226, 230, 0.6)',
    transition: 'all 0.2s ease-in-out',
    maxWidth: '500px',
    ':hover': {
        backgroundColor: 'rgba(248, 249, 250, 0.95)',
        border: '1px solid rgba(222, 226, 230, 0.8)'
    }
}

const checkboxStyle: CSSProperties = {
    marginRight: '12px',
    transform: 'scale(1.1)',
    accentColor: '#3A54B4',
    cursor: 'pointer'
}

const labelStyle: CSSProperties = {
    fontSize: '14px',
    color: Tgcc.Styles.darkTextColor,
    cursor: 'pointer',
    userSelect: 'none',
    fontFamily: 'CenturyGothic, Arial, sans-serif',
    lineHeight: '1.4',
    fontWeight: '400'
}

const adminLabelStyle: CSSProperties = {
    ...labelStyle,
    fontStyle: 'italic',
    color: '#6c757d',
    fontSize: '13px'
}

interface Props {
    state: Tgcc.GlobalState;
    dispatch: any;
    onPreferenceChange?: (enabled: boolean) => void;
}

interface State {
    proofConfirmationEmailsEnabled: boolean;
    isLoading: boolean;
}

export class ProofConfirmationEmailToggle extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            proofConfirmationEmailsEnabled: true,
            isLoading: false
        };
    }

    componentDidMount() {
        this.loadUserPreference();
    }

    componentDidUpdate(prevProps: Props) {
        // If user info changes (e.g., when impersonating), reload preference
        if (prevProps.state.UserInfo?.email !== this.props.state.UserInfo?.email) {
            this.loadUserPreference();
        }
    }

    loadUserPreference = () => {
        const { state } = this.props;
        const { LoginData, UserInfo } = state;

        if (!LoginData || !UserInfo?.email) {
            return;
        }

        this.setState({ isLoading: true });

        // If admin is impersonating, get the specific user's preference
        if (LoginData.impersonate) {
            Request.getProofConfirmationEmailPreference(
                LoginData,
                UserInfo.email,
                (data) => {
                    this.setState({
                        proofConfirmationEmailsEnabled: data.proofConfirmationEmailsEnabled,
                        isLoading: false
                    });
                },
                (err) => {
                    console.error('Failed to load proof confirmation email preference:', err);
                    this.setState({ isLoading: false });
                }
            );
        } else {
            // For regular users, use the preference from UserInfo
            this.setState({
                proofConfirmationEmailsEnabled: UserInfo.proofConfirmationEmailsEnabled ?? true,
                isLoading: false
            });
        }
    }

    handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const enabled = event.target.checked;
        this.setState({ proofConfirmationEmailsEnabled: enabled });

        // Notify parent component of the change
        if (this.props.onPreferenceChange) {
            this.props.onPreferenceChange(enabled);
        }
    }

    updateUserPreference = async (): Promise<void> => {
        const { state } = this.props;
        const { LoginData, UserInfo } = state;

        if (!LoginData || !UserInfo?.email) {
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            Request.updateProofConfirmationEmailPreference(
                LoginData,
                UserInfo.email,
                this.state.proofConfirmationEmailsEnabled,
                () => {
                    // Update the UserInfo in Redux state
                    const updatedUserInfo = {
                        ...UserInfo,
                        proofConfirmationEmailsEnabled: this.state.proofConfirmationEmailsEnabled
                    };
                    this.props.dispatch({
                        type: Tgcc.StateAction.SetUserInfo,
                        payload: updatedUserInfo
                    });
                    resolve();
                },
                (err) => {
                    console.error('Failed to update proof confirmation email preference:', err);
                    reject(err);
                }
            );
        });
    }

    render() {
        const { state } = this.props;
        const { LoginData, UserInfo } = state;
        const isAdmin = LoginData?.impersonate || UserInfo?.isAdmin;
        const isImpersonating = LoginData?.impersonate;

        if (!UserInfo?.email) {
            return null;
        }

        const labelText = isImpersonating 
            ? `Send proof confirmation emails to ${UserInfo.email}`
            : "Send me confirmation emails when proofs are submitted";

        return (
            <div style={checkboxContainerStyle}>
                <input
                    type="checkbox"
                    id="proofConfirmationEmails"
                    checked={this.state.proofConfirmationEmailsEnabled}
                    onChange={this.handleCheckboxChange}
                    disabled={this.state.isLoading}
                    style={checkboxStyle}
                />
                <label 
                    htmlFor="proofConfirmationEmails" 
                    style={isAdmin ? adminLabelStyle : labelStyle}
                >
                    {labelText}
                </label>
                {this.state.isLoading && (
                    <span style={{ 
                        marginLeft: '12px', 
                        fontSize: '12px', 
                        color: '#6c757d',
                        fontStyle: 'italic',
                        opacity: '0.8'
                    }}>
                        Loading...
                    </span>
                )}
            </div>
        );
    }
} 